import { create } from 'zustand'
import type { Session } from '@supabase/supabase-js'
import * as Sentry from '@sentry/react'
import { supabase } from '../lib/supabase'
import type { User, Business } from '../types'
import { usePlatformStore } from './platformStore'
import { useScheduleViewStore } from './scheduleViewStore'

interface AuthState {
  session: Session | null;
  user: User | null;
  business: Business | null;
  loading: boolean;
  error: string | null;
  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  patchBusiness: (patch: Partial<Business>) => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  user: null,
  business: null,
  loading: true,
  error: null,

  initialize: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (session) {
        await fetchUserAndBusiness(session, set);
      } else {
        set({ loading: false });
      }

      supabase.auth.onAuthStateChange(async (_event, session) => {
        try {
          if (session) {
            await fetchUserAndBusiness(session, set);
          } else {
            set({ session: null, user: null, business: null, loading: false });
          }
        } catch (e) {
          Sentry.captureException(e, { tags: { source: 'admin.authStore.onAuthStateChange' } });
        }
      });
    } catch (e) {
      Sentry.captureException(e, { tags: { source: 'admin.authStore.initialize' } });
      set({ error: 'Failed to initialize session', loading: false });
    }
  },

  signIn: async (email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        Sentry.captureException(error, { tags: { source: 'admin.authStore.signIn' } });
        set({ error: error.message, loading: false });
      }
    } catch (e) {
      Sentry.captureException(e, { tags: { source: 'admin.authStore.signIn.throw' } });
      set({ error: 'Sign in failed', loading: false });
    }
  },

  signInWithGoogle: async () => {
    // Session 168 §Auth.OAuthSignupPlacesAutocomplete Phase D.1 — admin
    // Google OAuth via redirect. Operator lands at /dashboard post-auth;
    // onAuthStateChange routes through fetchUserAndBusiness like normal.
    set({ loading: true, error: null });
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: window.location.origin + '/dashboard' },
      });
      if (error) {
        Sentry.captureException(error, { tags: { source: 'admin.authStore.signInWithGoogle' } });
        set({ error: error.message, loading: false });
      }
      // On success, browser is redirecting — no further state work here.
    } catch (e) {
      Sentry.captureException(e, { tags: { source: 'admin.authStore.signInWithGoogle.throw' } });
      set({ error: 'Could not start Google sign-in', loading: false });
    }
  },

  signOut: async () => {
    try {
      await supabase.auth.signOut();
      usePlatformStore.getState().clearLens();
      useScheduleViewStore.getState().clear();
      Sentry.setUser(null);
      set({ session: null, user: null, business: null });
    } catch {
      const current = get();
      set({ error: 'Sign out failed', session: current.session });
    }
  },

  patchBusiness: (patch) => {
    const current = get().business;
    if (!current) return;
    set({ business: { ...current, ...patch } });
  },
}));

async function fetchUserAndBusiness(
  session: Session,
  set: (state: Partial<AuthState>) => void
) {
  try {
    // §M6Setup Part 1A: route through get_public_user_id RPC instead of a
    // direct auth_id lookup. The RPC qualifies by get_user_business_id() so
    // it returns the public.users.id for the active business — deterministic
    // for multi-membership accounts. Mirrors mobile authStore Phase B.1.3.
    //
    // For multi-membership users with no active session row yet, the RPC
    // returns NULL → we set user/business to null and let the outer
    // <RequiresActiveBusinessGate> wrapper render the BusinessPickerEmptyState.
    const { data: publicUserIdResult, error: rpcError } = await supabase.rpc('get_public_user_id');

    if (rpcError) {
      // Transient infra error — preserve any existing state. Surface to Sentry.
      Sentry.captureException(rpcError, { tags: { source: 'fetchUserAndBusiness.rpc' } });
      set({ session, loading: false });
      return;
    }

    // Fallback path for multi-membership without an active session row:
    // pick any membership row so AuthGate passes through. The route-level
    // <RequiresActiveBusinessGate> then surfaces BusinessPickerEmptyState
    // and lets the operator choose. After picker selection, setActiveBusiness
    // writes user_session_context → next fetchUserAndBusiness call resolves
    // through the RPC deterministically.
    let userQuery = supabase
      .from('users')
      .select('id, auth_id, business_id, first_name, last_name, email, role, employment_type, phone, avatar_url, status, hourly_rate, created_at, is_platform_admin');

    if (publicUserIdResult != null) {
      userQuery = userQuery.eq('id', publicUserIdResult);
    } else {
      userQuery = userQuery
        .eq('auth_id', session.user.id)
        .not('business_id', 'is', null)
        .order('created_at', { ascending: true })
        .limit(1);
    }

    const { data: userData, error: userError } = await userQuery.maybeSingle();

    if (userError || !userData) {
      set({ session, user: null, business: null, loading: false });
      return;
    }

    let business: Business | null = null;
    if (userData.business_id) {
      const { data: bizData } = await supabase
        .from('businesses')
        .select('id, name, slug, industry, address, city, province, postal_code, phone, email, timezone, tier, modules_enabled, business_rules, vocabulary, country, region_code, ot_warning_threshold, require_gps_checkin, checkin_radius_m, require_ot_approval, payroll_addon, stripe_customer_id, stripe_subscription_id, trial_ends_at, lifetime_people_count, created_at, stripe_connect_account_id, stripe_connect_onboarding_complete, stripe_connect_charges_enabled, stripe_connect_payouts_enabled, stripe_connect_onboarded_at, client_language, auto_translate_messages, business_code, code_last_regenerated_at, code_regeneration_count, code_regen_year_start, schedule_config, owner_id, onboarding_answers, tax_mode, enable_dynamic_pricing')
        .eq('id', userData.business_id)
        .maybeSingle();

      business = bizData;
    }

    Sentry.setUser({ id: userData.id, email: userData.email });
    if (business) {
      Sentry.setTag('business_id', business.id);
      Sentry.setTag('tier', business.tier ?? 'free');
      Sentry.setTag('industry', business.industry ?? 'unknown');
    }

    set({
      session,
      user: userData as User,
      business,
      loading: false,
      error: null,
    });
  } catch {
    set({ session, user: null, business: null, loading: false, error: 'Failed to load user data' });
  }
}
